import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { user } from 'ampersand-app'

import lang from '@/lang'
import config from '@/config'

import DashboardTodoFileListComponent from '@/modules/dashboard/view/dashboard-todo-file-list.component'
import { ButtonIconSquare, DEBOUNCE_TIMES, SquareIconButton } from '@/modules/base/components/buttons/index'
import SimpleDrawer from '@/components/drawers/simple-drawer'
import { ConfirmModal } from '@/modules/base/components/modal/index'
import DocumentDetailPreview from '@/modules/document/components/edit/document-detail-preview'
import MultipleListing from '@/modules/base/components/listing/multiple'
import TodoMessages from './todo-messages/todo-messages'
import TodoFile from './todo-files/todo-file'
import IconClose from '@assets/images/icons/v2/close.svg'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'

import FileUploadChooser from '@/components/controls/file-upload-chooser'
import { toast } from '@/modules/base/components/toast/toastConfig'
import getUploader from '@/modules/file/uploader'

import { formatDate } from '@/modules/_helpers/formatDate'
import FileService from '@/modules/file/fileService'
import { downloadFile } from '@/modules/_helpers/downloadFile'
import TodoStatusEnum from '@/enums/TodoStatus.enum'
import TodoService from '@/modules/todo/todoService'
import TodoIntervalEnum from '@/enums/todoInterval.enum'

import IconCheck from '@assets/images/icons/check.svg'
import IconEdit from 'assets/images/icons/pencil.svg'
import IconDelete from 'assets/images/icons/trashcan.svg'

import { refreshList } from '@/redux/ducks/lists/requests/todoList'

import todoConfig from '@/modules/todo/config'
import customDecodeUri from '@/utils/customDecodeUri'
import { experimentalFeatures, FILE_LIST_FILTER, ROLES, errorCodes } from '@/modules/base/util/constants'
import handleUploadError from '@/modules/file/_helpers/handleUploadError'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import { TextReadMore } from '@/modules/base/components/texts'
import DocumentsUploadTrigger from '@/modules/document/components/upload/documents-upload-trigger'
import { addFiles } from '@/redux/ducks/document/upload'
import { isDmsUploadEnabled, createDmsUploader, executeUpload } from '@/redux/ducks/document/uploadHelpers'
import { withAsyncData } from '@/utils/withAsyncData'
import { useNavigate } from 'react-router'
import InfoTooltip from './todo-form/todo-form-file-upload-info'
import { useOpenModal } from '@/modules/_helpers/openModal'
import TodoDeleteConfirmationModal from '@/modules/todo/components/todo-delete-confirmation-modal'
import { hasPendingUploads, getUploadProgress } from '@/modules/todo/_helpers/todoUploadHelpers'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import IconInfo from '@assets/images/icons/info.svg'

const { dateFormat: { api: apiDateFormat } } = config

const useStyles = makeStyles(theme => ({
  header: {
    height: 86,
    padding: '0px 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: theme.border,
    borderTop: theme.border,
    flexShrink: 0,
    backgroundColor: theme.backgroundColor,
  },
  headline: {
    fontSize: 20,
    lineHeight: '26px',
    letterSpacing: '0.2px',
    flexGrow: 1,
  },
  preview: {
    height: '100%',
    width: 674,
    '& > div': {
      height: '100%',
    },
  },
  signatureContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: 500,
    height: '100%',
    overflow: 'hidden',
    borderLeft: '1px solid #dee2ed',
    '& p.disclaimer': {
      fontSize: '10px',
      color: theme.textLight,
      textAlign: 'right',
      marginRight: 30,
    },
  },
  close: {
    cursor: 'pointer',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    '&.main': {
      overflow: 'auto',
    },
    marginTop: 25,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    '& + $row': {
      marginTop: 20,
    },
    '& $rowItem': {
      width: '50%',
    },
  },
  section: {
    margin: '0 30px',
    paddingBottom: '20px',
    '& + $section': {
      paddingTop: 20,
      borderTop: `1px solid ${theme.borderColor}`,
    },
  },
  rowItem: {
    padding: 0,
  },
  content: {
    fontSize: 16,
    wordWrap: 'break-word',
  },
  contentTitle: {
    paddingBottom: 20,
    fontSize: 16,
    wordWrap: 'break-word',
  },
  description: {
    fontSize: 14,
    wordWrap: 'break-word',
    paddingBottom: 20,
  },
  fileItemRow: {
    width: '100%',
    alignItems: 'center',
  },
  fileDropDown: {
    paddingTop: 10,
    marginBottom: 10,
  },
  title: {
    textTransform: 'uppercase',
    fontSize: 13,
    color: theme.textLighter,
  },
  documentsSectionTitle: {
    fontSize: 12,
    lineHeight: '18px',
    letterSpacing: '0.4px',
    fontWeight: theme.fontWeights.semiBold,
    marginBottom: 10,
  },
  footer: {
    marginLeft: 30,
    marginRight: 30,
    paddingBottom: 20,
    paddingTop: 20,
    display: 'flex',
    justifyContent: 'end',
    '& p': {
      fontSize: '10px',
      color: theme.textLight,
    },
  },
  footerLeft: {
    display: 'flex',
    marginRight: 'auto',
    gap: 10,
  },
  actionButton: {
    marginLeft: 5,
  },
  fileChooser: {
    '& .title': {
      fontSize: 14,
      padding: 0,
      margin: 0,
    },
    '& .subTitle': {
      fontSize: 12,
      lineHeight: 'unset',
    },
    '& .special': {
      fontWeight: 400,
    },
  },
  fileTypes: {
    fontSize: 12,
    color: theme.textMuted,
    marginTop: 10,
    maxWidth: 300,
  },
  accessDeniedBanner: {
    backgroundColor: theme.errorBackground,
    borderRadius: 8,
    padding: '12px 14px 14px',
    marginBottom: 14,
  },
  accessDeniedBannerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  accessDeniedBannerHeadline: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
  },
  accessDeniedBannerTitle: {
    '& path': {
      fill: theme.errorText,
    },
  },
  accessDeniedBannerTitleText: {
    color: theme.errorText,
    fontWeight: theme.fontWeights.bold,
    fontSize: 15,
    lineHeight: '22px',
  },
  accessDeniedBannerMessage: {
    color: theme.errorText,
    fontSize: 14,
    lineHeight: '21px',
    maxWidth: 420,
  },
  accessDeniedBannerClose: {
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 0,
    margin: 0,
    cursor: 'pointer',
  },
  accessDeniedBannerCloseIcon: {
    '& path': {
      fill: theme.errorText,
    },
  },
  creatorName: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  drawer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
  },
  todoFileList: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
  },
  tooltipText: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: theme.fontSizes.smaller,
    margin: 0,
    color: theme.lighterBlue,
    fontStyle: 'italic',
    cursor: 'pointer',
  },
}))

const TodoDetail = (props) => {
  const navigate = useNavigate()
  const { id, onEdit, onClose, todo: originalTodo, readOnly: readOnlyProp = false, inactiveSubscription = false } = props
  const classes = useStyles(props)
  const openModal = useOpenModal()

  const [isFileContainerOpen, setIsFileContainerOpen] = useState(false)
  const [todo, setTodo] = useState(originalTodo)
  const [, setDocumentsToLink] = useState([])
  const [fileLinkActive, setFileLinkActive] = useState(false)
  const [fileToUnlink, setFileToUnlink] = useState(null)
  const [documentToUnlink, setDocumentToUnlink] = useState(null)
  const [accessDeniedBannerDismissed, setAccessDeniedBannerDismissed] = useState(false)
  const [showAccessDeniedBannerFromAction, setShowAccessDeniedBannerFromAction] = useState(false)
  const [previewFile, setPreviewFile] = useState({ visible: false, file: {} })
  const [previewDocument, setPreviewDocument] = useState({ visible: false, document: {} })
  const dispatch = useDispatch()
  const userId = useSelector(data => data.user.info.id)
  const todoUploads = useSelector(state => state.document.upload.todoUploads)
  const subscriptions = useSelector(state => state.subscription.details.subscriptions)
  const hasActiveSubscription = subscriptions.some(subscription => subscription.end === null || moment(subscription.end).isAfter(moment()))

  const [isReadOnly, setIsReadOnly] = useState(true)
  const isCreator = todo?.creator?.id === userId
  const isCompleted = todo?.status === TodoStatusEnum.DONE || todo?.status === TodoStatusEnum.DECLINED
  const isResponsibleUser = todo?.users?.some(u => u.id === userId)
  const isOwner = (user?.roles || []).includes(ROLES.OWNER_ROLE)
  const isAdmin = (user?.roles || []).includes(ROLES.ADMIN_ROLE)
  const isFolderPermissionsEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.folderPermissions)
  const showEditButton = !isReadOnly && !isCompleted && isCreator && Boolean(onEdit) && !hasPendingUploads(todo?.id, todoUploads)
  const disableActionButtons = (!isResponsibleUser || isCompleted || isReadOnly || hasPendingUploads(todo?.id, todoUploads)) && (!isOwner && !isAdmin)
  const isResponsibleUserTaxConsultant = todo?.users?.some(user =>
    user.tenantUsers?.some(tenant =>
      tenant.userRoles?.includes('TAX_CONSULTANT'),
    ),
  )
  const hasResponsibleFiles = !isResponsibleUser && todo?.files?.some(file => todo.users.some(user => user.id === file.createdBy))
  const canShowDeleteButton = !isCompleted && isCreator
  const isDeleteAllowed = !isReadOnly && !isResponsibleUserTaxConsultant && !todo?.messageCount && !hasResponsibleFiles

  const [previewZoom, setPreviewZoom] = useState(1)

  const zoom = (zoomStep) => {
    setPreviewZoom(Math.max(previewZoom + zoomStep, 0.25))
  }

  const preview = previewFile.visible
    ? previewFile.file
    : previewDocument.visible && previewDocument.document
  const documents = todo?.documents ?? []
  const files = todo?.files?.filter((file) => !documents.map(({ name }) => name).includes(file.name)) ?? []
  const hasInaccessibleFiles = isFolderPermissionsEnabled
    && (files.some(file => file.accessDenied === true) || documents.some(doc => doc.accessDenied === true))
  const shouldShowAccessDeniedBanner = (hasInaccessibleFiles || showAccessDeniedBannerFromAction) && !accessDeniedBannerDismissed

  const setSeen = async() => {
    await TodoService.markAsSeen(todo, false)
    dispatch(refreshList())
  }

  const finalizeTodo = async() => {
    await TodoService.done(todo.id).then(updatedTodo => {
      setTodo(updatedTodo)
      return setSeen()
    })
  }

  useWebSocketEvent(EventTypes.TODO_CRUD, ({ data }) => {
    if (data?.todoId === id) {
      loadTodo()
    }
  })

  const loadTodo = async() => {
    try {
      const todo = await TodoService.getById(id)
      setTodo(todo)
    } catch (error) {
      let message = lang.defaultErrorMessage
      if (error?.body?.meta?.tenant[0]?.code === errorCodes.NOT_ALLOWED) {
        message = lang.todo.collaborationDeletedErrorMessage
      }

      toast.showToast({ type: 'error', message })
    }
  }

  const isAccessDeniedError = (error) => {
    const statusCode = error?.status || error?.statusCode || error?.body?.status || error?.body?.statusCode
    const resourceCode = error?.body?.meta?.resource?.[0]?.code
    return statusCode === 403 || resourceCode === errorCodes.NOT_ALLOWED
  }

  const handleAttachmentError = (error) => {
    if (isFolderPermissionsEnabled && isAccessDeniedError(error)) {
      setAccessDeniedBannerDismissed(false)
      setShowAccessDeniedBannerFromAction(true)
      return
    }
    toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
  }

  useEffect(() => {
    if (id) {
      loadTodo()
    }
  }, [id, documentToUnlink, fileToUnlink])

  useEffect(() => {
    setAccessDeniedBannerDismissed(false)
    setShowAccessDeniedBannerFromAction(false)
  }, [todo?.id])

  useEffect(() => {
    if (todo && !readOnlyProp) {
      setIsReadOnly([TodoStatusEnum.DONE, TodoStatusEnum.DECLINED].includes(todo.status)
        || todo.deletedAt !== null
        || todo.isReadOnly
        || !hasActiveSubscription,
      )
    }
  }, [todo, hasActiveSubscription])

  const buildName = (user) => {
    return (user?.firstName || user?.lastName)
      ? `${user?.firstName} ${user?.lastName}`
      : user?.email || user?.invitationEmail
  }

  const onGoToCustomerClick = ({ id }) => {
    navigate(`/customer/${id}`)
  }

  const onGoToSupplierClick = ({ id }) => {
    navigate(`/supplier/${id}`)
  }

  const onReject = () => {
    if (!isReadOnly) {
      TodoService.decline(todo.id).then(updatedTodo => {
        setTodo(updatedTodo)
        return setSeen()
      })
    }
  }

  const onDelete = async() => {
    if (!isDeleteAllowed) return

    openModal((close) => ({
      customComponent: TodoDeleteConfirmationModal,
      props: {
        todo,
        onClose: close,
        onSuccess: async() => {
          dispatch(refreshList())
          close()
          onClose()
        },
      },
    }))
  }

  const onDownloadFile = async id => {
    try {
      const { body: data } = await FileService.fetchFileInfo(id)
      downloadFile(data.url, customDecodeUri(data.name), data.mimetype)
    } catch (error) {
      handleAttachmentError(error)
    }
  }

  const onDownloadDocument = async id => {
    try {
      const data = await TodoService.fetchDocumentInfo(id)
      downloadFile(data.url, customDecodeUri(data.name), data.mimetype)
    } catch (error) {
      handleAttachmentError(error)
    }
  }

  const onCloseFilePreview = () => {
    const { previewControlHandlers } = previewFile
    if (previewControlHandlers) {
      previewControlHandlers.handleClosePreview()
    }
    setPreviewFile({ visible: false, file: {}, previewControlHandlers: undefined })
  }

  const onCloseDocumentPreview = () => {
    const { previewControlHandlers } = previewDocument
    if (previewControlHandlers) {
      previewControlHandlers.handleClosePreview()
    }
    setPreviewDocument({ visible: false, document: {}, previewControlHandlers: undefined })
  }

  const linkDocuments = async() => {
    setDocumentsToLink(prevItems => {
      if (prevItems.length) {
        TodoService.link(
          todo.id,
          prevItems.map(id => TodoService.createRelationItem(id, TodoService.TYPE_DOCUMENT)),
        ).then(setTodo)
      }

      return []
    })
  }

  const onPreviewFile = async(file, previewControlHandlers) => {
    if (fileLinkActive === false) {
      if (isFileContainerOpen) {
        setPreviewFile({ file, visible: true, previewControlHandlers })
      } else {
        try {
          const { body: data } = await FileService.fetchFileInfo(file.id, todo?.tenantCollaboration?.id)
          setPreviewFile({ file: data, visible: true, previewControlHandlers: null })
        } catch (error) {
          handleAttachmentError(error)
        }
      }
    }
  }

  const onPreviewDocument = async(document) => {
    try {
      const data = await TodoService.fetchDocumentInfo(document.id)
      setPreviewDocument({ document: data, visible: true, previewControlHandlers: null })
    } catch (error) {
      handleAttachmentError(error)
    }
  }

  const onUnlinkFile = () => {
    setFileToUnlink(file => {
      if (file) {
        if (file.id === preview.id) {
          onCloseFilePreview()
        }
        TodoService.unlink(
          todo.id,
          [TodoService.createRelationItem(file.id, TodoService.TYPE_FILE)],
        ).then(updatedTodo => {
          setTodo(updatedTodo)
          setSeen()
        })
      }
      return null
    })
  }

  const onUnlinkDocument = () => {
    setDocumentToUnlink(document => {
      if (document) {
        TodoService.removeDocument(document.id).then(updatedTodo => {
          setTodo(updatedTodo)
          setSeen()
        })
      }

      if (document.id === preview.id) {
        onCloseDocumentPreview()
      }

      return null
    })
  }

  const onSelectFromFileContainer = (file) => {
    setFileLinkActive(true)
    TodoService.link(
      todo.id,
      [TodoService.createRelationItem(file.id, TodoService.TYPE_FILE)],
    ).then(updatedTodo => {
      setTodo(updatedTodo)
      setSeen()
    }).finally(() => {
      setTimeout(() => {
        setFileLinkActive(false)
      }, 1000)
      setIsFileContainerOpen(false)
    })
  }

  const closeFileContainer = () => {
    setIsFileContainerOpen(false)
  }

  const onComplete = (id, fileName, response) => {
    if (!response.success) return
    const { data: [{ id: fileId }] } = response
    setDocumentsToLink(prev => {
      return [...prev, [fileId]]
    })
  }

  const onFileUploadError = (id, name, errorReason, xhr) => {
    handleUploadError(xhr, errorReason)
  }

  const uploader = getUploader({
    autoUpload: true,
    callbacks: {
      onComplete,
      onError: onFileUploadError,
      onAllComplete: () => linkDocuments(),
    },
    request: {
      endpoint: todoConfig.uploadUrl,
      customHeaders: { authorization: `Bearer ${user?.token}` },
    },
  })

  const openFileContainer = event => {
    event.preventDefault()
    event.stopPropagation()
    setIsFileContainerOpen(true)
  }

  const copyTodoFile = async id => {
    await TodoService.copyDocument(id)
    toast.showToast({ type: 'info', message: lang.todo.documentCopied })
  }

  const onDone = () => {
    if (!isReadOnly) {
      finalizeTodo()
    }
  }

  const handleDmsUpload = async(batchItems, e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (batchItems.length > 25) {
      toast.showToast({ type: 'error', message: lang.fileUploaderTooManyItemsError(25) })
      return
    }

    try {
      const files = batchItems.map(item => item.file)
      await dispatch(addFiles(files))

      const uploadParams = {
        params: {
          syncToMbk: false,
          metaData: {},
          recordDate: moment().format(apiDateFormat),
          tags: [],
          notes: '',
          folderId: null,
        },
        files,
        scan: false,
        closeOverlayAfterComplete: true,
        source: 'todo',
        todoId: todo.id,
        onFileUploaded: (fileId) => {
          TodoService.link(
            todo.id,
            [TodoService.createRelationItem(fileId, TodoService.TYPE_FILE)],
          )
        },
      }

      const result = await executeUpload(uploadParams)

      if (result?.data) {
        setSeen()
      } else {
        throw new Error('Invalid upload result structure')
      }
    } catch (error) {
      toast.showToast({ type: 'error', message: lang.todo.createTaskErrorMessage })
    }
  }

  const dmsUploader = createDmsUploader(handleDmsUpload, uploader)
  const useDmsUpload = isDmsUploadEnabled()

  const fileListControl = (
    <>
      {Boolean(files?.length) || Boolean(documents.length) || !isReadOnly
        ? (
          <div className={clsx(classes.title, classes.documentsSectionTitle)}>{lang.todo.detail.documents}</div>
        )
        : null
      }
      {shouldShowAccessDeniedBanner && (
        <div className={classes.accessDeniedBanner}>
          <div className={classes.accessDeniedBannerHeader}>
            <div className={classes.accessDeniedBannerHeadline}>
              <ErrorOutlineIcon className={classes.accessDeniedBannerTitle} style={{ width: 20, height: 20 }} />
              <span className={classes.accessDeniedBannerTitleText}>{lang.todo.detail.accessDeniedTitle}</span>
            </div>
            <button
              className={classes.accessDeniedBannerClose}
              onClick={() => setAccessDeniedBannerDismissed(true)}
              type='button'
              aria-label={lang.closeButtonLabel}
            >
              <SVGIcon svg={IconClose} width='12px' height='12px' customClass={classes.accessDeniedBannerCloseIcon} />
            </button>
          </div>
          <div className={classes.accessDeniedBannerMessage}>
            {lang.todo.detail.accessDeniedMessage}
          </div>
        </div>
      )}
      {Boolean(documents.length) && documents.map(document => {
        return (
          <TodoFile
            currentPreview={preview}
            key={`D${document.id}`}
            file={document}
            className={classes.fileItemRow}
            onDownLoad={(isFolderPermissionsEnabled && document.accessDenied === true) ? undefined : id => onDownloadDocument(id)}
            onPreview={(isFolderPermissionsEnabled && document.accessDenied === true) ? undefined : (id, name) => onPreviewDocument({ id, name }, null)}
            onUnlink={(id, name) => setDocumentToUnlink({ id, name })}
            onMove={(todo.status === TodoStatusEnum.OPEN) ? undefined : copyTodoFile}
            readOnly={todo.creator?.id !== userId || (isFolderPermissionsEnabled && document.accessDenied === true)}
          />
        )
      })}
      {Boolean(files?.length) && files.map(file => {
        return (
          <TodoFile
            currentPreview={preview}
            key={`F${file.id}`}
            file={file}
            className={classes.fileItemRow}
            onDownLoad={(isFolderPermissionsEnabled && file.accessDenied === true) ? undefined : id => onDownloadFile(id)}
            onPreview={(isFolderPermissionsEnabled && file.accessDenied === true) ? undefined : (id, name) => onPreviewFile({ id, name }, null)}
            onUnlink={(id, name) => setFileToUnlink({ id, name })}
            readOnly={todo.creator?.id !== userId || (isFolderPermissionsEnabled && file.accessDenied === true)}
          />
        )
      })}
      {(!isReadOnly) && (
        <div className={clsx(classes.fileDropDown)}>
          <SimpleDrawer
            className={classes.drawer}
            open={isFileContainerOpen}
            enableBackdropClick
            onClose={closeFileContainer}
            headline={lang.todo.uploader.title}
          >
            <DashboardTodoFileListComponent
              tenantCollaborationId={todo?.tenantCollaboration?.id}
              className={classes.todoFileList}
              onClose={closeFileContainer}
              onSelect={onSelectFromFileContainer}
              onPreview={onPreviewFile}
              filter={FILE_LIST_FILTER.NO_SYSTEM_DOCUMENTS}
            />
          </SimpleDrawer>
          {useDmsUpload
            ? (
              <DocumentsUploadTrigger
                multiple
                onSubmit={(files, e) => handleDmsUpload(files, e)}
                disabled={false}
              >
                <FileUploadChooser
                  multiple
                  uploader={dmsUploader}
                  onOpenFileContainer={openFileContainer}
                  className={classes.fileChooser}
                >
                  <div className={classes.fileTypes}>
                    <InfoTooltip />
                  </div>
                </FileUploadChooser>
              </DocumentsUploadTrigger>
            )
            : (
              <FileUploadChooser
                uploader={uploader}
                multiple
                onOpenFileContainer={openFileContainer}
                className={classes.fileChooser}
              >
                <div className={classes.fileTypes}>
                  <InfoTooltip />
                </div>
              </FileUploadChooser>
            )}
        </div>
      )}
    </>
  )

  const filenameEllipse = '[...]'
  const maxFileNameLength = 40

  const getShortFileName = (file) => {
    if (file && file.name) {
      const fileName = customDecodeUri(file.name)

      if (fileName.length > maxFileNameLength) {
        let suffix = fileName.slice(-4)
        if (suffix[0] !== '.') { suffix = fileName.slice(-5) }
        const shortFileName = fileName.slice(0, -4).slice(0, maxFileNameLength)
        return ` "${shortFileName}${filenameEllipse}${suffix}"`
      }

      return fileName
    }

    return null
  }

  const fileToUnlinkName = getShortFileName(fileToUnlink)
  const documentToUnlinkName = getShortFileName(documentToUnlink)

  const responsibles = todo?.users?.map(user => ({
    id: userId,
    name: buildName(user),
  }))

  const customers = todo?.customers?.map(customer => ({
    id: customer.id,
    name: customer.name,
  }))

  const suppliers = todo?.suppliers?.map(supplier => ({
    id: supplier.id,
    name: supplier.name,
  }))

  const handleEditClick = () => {
    if (hasPendingUploads(todo?.id, todoUploads)) {
      const progress = getUploadProgress(todo.id, todoUploads)
      toast.showToast({
        type: 'info',
        message: lang.todo.uploadInProgressMessage(progress),
      })
      return
    }

    onEdit && onEdit()
  }

  return todo
    ? (
      <div className={classes.signatureContainer}>
        {preview && (<div className={classes.preview}>
          <DocumentDetailPreview
            onClose={() => { onCloseDocumentPreview(); onCloseFilePreview() }}
            hideControls
            file={preview}
            onZoom={zoom}
            disableOcr
            downloadLabel={lang.generics.download}
            downloadUrl={`${preview.url}?download=true&token=${user.token}`}
            zoom={previewZoom} />
        </div>
        )}
        {!preview && <div className={classes.root}>
          <div className={classes.header}>
            <span className={classes.headline}>{lang.todo.detail.title}</span>
            <SVGIcon onClick={onClose} className={classes.close} svg={IconClose} width="12px" height="12px" />
          </div>
          <div className={clsx(classes.container, 'main')}>
            <div className={classes.container}>
              <div className={classes.section}>
                <div className={classes.contentTitle}>
                  {todo.title}
                </div>

                {todo.content && (
                  <div className={clsx(classes.rowItem)}>
                    <div className={classes.title}>
                      {lang.generics.description}
                    </div>
                    <div className={classes.description}>
                      <TextReadMore height="120px" readLessLabel={lang.showLess} readMoreLabel={lang.showMore}>
                        {(todo.content).split(/\n/g).map((line) => {
                          return line
                            ? (
                              <div className={classes.content}>{line}</div>
                            )
                            : (
                              <div className={classes.content}>&nbsp;</div>
                            )
                        })}
                      </TextReadMore>
                    </div>
                  </div>
                )}
              </div>
              <div className={classes.section}>
                <div className={classes.row}>

                  <div className={clsx(classes.rowItem)}>
                    <div className={classes.title}>{lang.todo.detail.creator}</div>
                    <div className={classes.creatorName}>{buildName(todo?.creator)}</div>
                  </div>

                  {Boolean(responsibles?.length) && (
                    <div className={clsx(classes.rowItem)}>
                      <div className={classes.title}>{lang.todo.detail.responsibles(responsibles.length)}</div>
                      <div>
                        <MultipleListing
                          elements={responsibles}
                        />
                      </div>
                      {inactiveSubscription && (
                        <RichTooltip body={lang.todo.readOnlyModeTooltipBody} width='500px' placement={'top'}>
                          <p className={classes.tooltipText}>{lang.todo.readOnlyMode} <SVGIcon width={12} height={12} svg={IconInfo} /></p>
                        </RichTooltip>
                      )}
                    </div>)
                  }
                </div>

                <div className={classes.row}>
                  <div className={clsx(classes.rowItem)}>
                    <div className={classes.title}>{lang.todo.detail.dueFromDate}</div>
                    <div>
                      {todo.dueFromDate ? formatDate(todo.dueFromDate) : '-'}
                    </div>
                  </div>

                  <div className={clsx(classes.rowItem)}>
                    <div className={classes.title}>{lang.todo.detail.dueToDate}</div>
                    <div>{todo.dueToDate ? formatDate(todo.dueToDate) : '-'}</div>
                  </div>
                </div>

                <div className={classes.row}>
                  <div className={clsx(classes.rowItem)}>
                    <div className={classes.title}>{lang.todo.detail.interval}</div>
                    <div>{todo.todoIntervalTemplateId
                      ? lang.todo.intervals[todo.todoIntervalTemplate.interval]
                      : lang.todo.intervals[TodoIntervalEnum.SINGLE]}
                    </div>
                  </div>
                </div>

                {(Boolean(customers?.length) || Boolean(suppliers?.length)) && (
                  <div className={clsx(classes.row)}>
                    {Boolean(customers.length) && (
                      <div className={clsx(classes.rowItem)}>
                        <div className={classes.title}>{lang.todo.detail.customers(customers.length)}</div>
                        <div>
                          <MultipleListing
                            elements={customers}
                            onClick={onGoToCustomerClick}
                          />
                        </div>
                      </div>)
                    }

                    {Boolean(suppliers.length) && (
                      <div className={clsx(classes.rowItem)}>
                        <div className={classes.title}>{lang.todo.detail.suppliers(suppliers.length)}</div>
                        <div>
                          <MultipleListing
                            elements={suppliers}
                            onClick={onGoToSupplierClick}
                          />
                        </div>
                      </div>)
                    }
                  </div>
                )}
              </div>

              <div className={classes.section}>
                {fileListControl}
              </div>
            </div>
            {!todo.commentsDisabled && <TodoMessages todo={todo} onPreviewFile={onPreviewFile} readOnly={isReadOnly} />}

          </div>

          <div className={classes.footer}>
            <div className={classes.footerLeft}>
              {showEditButton && (
                <SquareIconButton
                  size='large'
                  icon={IconEdit}
                  onClick={handleEditClick}
                  tooltip={lang.editButtonLabel}
                />
              )}
              {canShowDeleteButton && (
                <SquareIconButton
                  size='large'
                  icon={IconDelete}
                  tooltip={isDeleteAllowed ? lang.deleteButtonLabel : lang.todo.detail.deleteButtonDisabledLabel}
                  disabled={!isDeleteAllowed}
                  onClick={onDelete}
                  tooltipIgnoreDisabled={true}
                />
              )}
            </div>
            <ButtonIconSquare
              customClass={classes.actionButton}
              label={lang.todo.deleteButtonLabel}
              variant='outline'
              onClick={onReject}
              disabled={isReadOnly || disableActionButtons}
            />
            <ButtonIconSquare
              debounceTime={DEBOUNCE_TIMES.EXTRA_LONG}
              customClass={classes.actionButton}
              icon={IconCheck}
              label={lang.todo.doneButtonLabels[todo.type]}
              onClick={onDone}
              disabled={isReadOnly || disableActionButtons}
            />
          </div>
          <ConfirmModal
            open={fileToUnlink !== null}
            title={lang.documentDeleteConfirmCaption}
            description={<span dangerouslySetInnerHTML={{ __html: lang.documentDeleteConfirmText(1).replace('{{document}}', fileToUnlinkName) }} />}
            onConfirm={onUnlinkFile}
            onAbort={() => { setFileToUnlink(null) }} />
          <ConfirmModal
            open={documentToUnlink !== null}
            title={lang.documentDeleteConfirmCaption}
            description={<span dangerouslySetInnerHTML={{ __html: lang.documentDeleteConfirmText(1).replace('{{document}}', documentToUnlinkName) }} />}
            onConfirm={onUnlinkDocument}
            onAbort={() => { setDocumentToUnlink(null) }} />
        </div>}
      </div>
    )
    : null
}

TodoDetail.propTypes = {
  todo: PropTypes.object,
  onEdit: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.string,
}

TodoDetail.defaultProps = {
  onEdit: undefined,
}

export default withAsyncData(TodoDetail, lang.todo.submenuTitle)
